import { Button, Select, Tree } from 'antd';
import {
  DownOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import style from './MetricTree.module.css';
import {
  $activeCity,
  $activeMetric,
  $activeYear,
  $showMetric,
  changeActiveMetricEv,
  changeActiveYearEv,
  changeMetricDetailsEv,
  changeShowMetricEv,
} from '../../models/logicModel/index.js';
import useWindowSize from '../../hooks/useWindowSize.js';
import { city_risks_dictionary } from '../../dictionaries/city_risks_dictionary.js';

const yearOptions = [
  {
    label: '2016',
    value: 2016,
  },
  {
    label: '2017',
    value: 2017,
  },
  {
    label: '2018',
    value: 2018,
  },
  {
    label: '2019',
    value: 2019,
  },
  {
    label: '2020',
    value: 2020,
  },
  {
    label: '2021',
    value: 2021,
  },
  {
    label: '2022',
    value: 2022,
  },
  {
    label: '2023',
    value: 2023,
  },
  {
    label: '2024',
    value: 2024,
  },
  {
    label: '2025',
    value: 2025,
  },
];

function MetricTree() {
  const showMetric = useUnit($showMetric);
  const changeShowMetric = useUnit(changeShowMetricEv);
  const changeActiveMetric = useUnit(changeActiveMetricEv);
  const activeMetric = useUnit($activeMetric);
  const activeYear = useUnit($activeYear);
  const changeActiveYear = useUnit(changeActiveYearEv);
  const [width, height] = useWindowSize();
  const changeMetricDetails = useUnit(changeMetricDetailsEv);
  const activeCity = useUnit($activeCity);
  const { t } = useTranslation();

  const onInfoClick = (e, metric) => {
    e.stopPropagation();
    changeMetricDetails(metric);
  };

  const treeData = [
    {
      title: t('population'),
      key: 'zoom9_population',
    },
    {
      title: t('floods_inundation'),
      key: '1',
      checkable: false,
      children: [
        {
          title: (
            <div className={style.tree_node}>
              {t('floods')}
              <InfoCircleOutlined onClick={(e) => onInfoClick(e, 'floods')} />
            </div>
          ),
          key: 'flood_ind',
        },
        {
          title: (
            <div className={style.tree_node}>
              {t('debris_flow')}
              <InfoCircleOutlined
                onClick={(e) => onInfoClick(e, 'mudfloods')}
              />
            </div>
          ),
          key: 'mudflow_ind',
        },
      ],
    },
    {
      title: (
        <div className={style.tree_node}>
          {t('drought')}
          <InfoCircleOutlined onClick={(e) => onInfoClick(e, 'ndvi')} />
        </div>
      ),
      key: '2',
      checkable: false,
      children: [
        {
          title: t('vegetation_index'),
          key: 'NDVI',
        },
      ],
    },
    {
      title: (
        <div className={style.tree_node}>
          {t('temperatures')}
          <InfoCircleOutlined onClick={(e) => onInfoClick(e, 'lst')} />
        </div>
      ),
      key: '3',
      checkable: false,
      children: [
        {
          title: t('lst_dif'),
          key: 'LST-DIF',
        },
        {
          title: t('lst'),
          key: 'LST',
        },
      ],
    },
    {
      title: (
        <div className={style.tree_node}>
          {t('air_quality')}
          <InfoCircleOutlined onClick={(e) => onInfoClick(e, 'air')} />
        </div>
      ),
      key: '5',
      checkable: false,
      children: [
        ...(activeCity === 2
          ? [
              {
                title: (
                  <div className={style.tree_node}>
                    PM2.5
                    <InfoCircleOutlined
                      onClick={(e) => onInfoClick(e, 'pm25')}
                    />
                  </div>
                ),
                key: 'pm25',
              },
            ]
          : []),
        {
          title: 'CO',
          key: 'AIR-CO',
        },
        {
          title: 'HCHO',
          key: 'AIR-HCHO',
        },
        {
          title: 'NO2',
          key: 'AIR-NO2',
        },
        {
          title: 'SO2',
          key: 'AIR-SO2',
        },
      ],
    },
    ...(activeCity
      ? city_risks_dictionary[activeCity].map((item) => {
          return {
            title: t(item),
            key: item,
            checkable: false,
            disabled: true,
          };
        })
      : []),
  ];

  const open = () => {
    changeShowMetric(true);
  };

  const close = () => {
    changeShowMetric(false);
  };

  const onCheck = (nodes, e) => {
    if (activeMetric === e.node.key) {
      changeActiveMetric('');
    } else {
      changeActiveMetric(e.node.key);
    }
  };

  return (
    <>
      <Button
        className={[style.open_button, 'glass'].join(' ')}
        style={{
          display: showMetric ? 'none' : 'flex',
        }}
        size="large"
        onClick={open}
        icon={<RightOutlined />}
        iconPosition="end"
      >
        {t('indicator')}
      </Button>
      <div
        className={[
          style.metric_tree,
          'glass',
          showMetric ? style.show : style.hide,
        ].join(' ')}
      >
        <div className={style.year_select_wrapper}>
          <div>{t('selected_year')}:</div>
          <Select
            options={yearOptions}
            className={style.year_select}
            placeholder="Выберите год"
            value={activeYear}
            onChange={changeActiveYear}
          />
        </div>
        <Tree
          treeData={treeData}
          checkable
          selectable={false}
          switcherIcon={<DownOutlined />}
          multiple={false}
          onCheck={onCheck}
          checkedKeys={[activeMetric]}
          defaultExpandAll
          height={height * 0.55}
        />
        <Button
          className={style.close_button}
          onClick={close}
          shape="circle"
          icon={<LeftOutlined />}
        />
      </div>
    </>
  );
}

export default MetricTree;
